import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Plus, Edit, Trash2, Loader2, Phone, Mail } from 'lucide-react';
import { staffAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({ name: '', role: 'server', email: '', phone: '', hourly_rate: '' });
  const { toast } = useToast();

  const fetchStaff = useCallback(async () => {
    try {
      const data = await staffAPI.getAll();
      setStaff(data);
    } catch (error) {
      toast({ title: 'Error loading staff', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null };
      if (editingStaff) {
        await staffAPI.update(editingStaff.id, data);
        toast({ title: 'Staff updated!' });
      } else {
        await staffAPI.create(data);
        toast({ title: 'Staff added!' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast({ title: 'Error saving staff', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await staffAPI.delete(id);
      toast({ title: 'Staff deleted!' });
      fetchStaff();
    } catch (error) {
      toast({ title: 'Error deleting staff', variant: 'destructive' });
    }
  };

  const openEdit = (s) => {
    setEditingStaff(s);
    setFormData({ name: s.name, role: s.role, email: s.email || '', phone: s.phone || '', hourly_rate: s.hourly_rate?.toString() || '' });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingStaff(null);
    setFormData({ name: '', role: 'server', email: '', phone: '', hourly_rate: '' });
  };

  const filteredStaff = roleFilter === 'all' ? staff : staff.filter(s => s.role === roleFilter);

  const roleColors = {
    chef: 'bg-red-100 text-red-800',
    server: 'bg-blue-100 text-blue-800',
    bartender: 'bg-purple-100 text-purple-800',
    setup: 'bg-green-100 text-green-800',
    manager: 'bg-yellow-100 text-yellow-800'
  };

  if (loading) return <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="staff-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Staff Management</h1>
          <p className="text-gray-600">Manage your team members</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="add-staff-btn">
              <Plus size={20} className="mr-2" />Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingStaff ? 'Edit Staff' : 'Add New Staff'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="staff-name-input" /></div>
              <div><Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="server">Server</SelectItem>
                    <SelectItem value="chef">Chef/Kitchen</SelectItem>
                    <SelectItem value="bartender">Bartender</SelectItem>
                    <SelectItem value="setup">Setup Crew</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="staff-email-input" /></div>
              <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="staff-phone-input" /></div>
              <div><Label>Hourly Rate ($)</Label><Input type="number" step="0.01" value={formData.hourly_rate} onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})} data-testid="staff-rate-input" /></div>
              <Button type="submit" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f]" data-testid="save-staff-btn">{editingStaff ? 'Update' : 'Add'} Staff</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={roleFilter} onValueChange={setRoleFilter} className="mb-6">
        <TabsList className="bg-white">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">All ({staff.length})</TabsTrigger>
          <TabsTrigger value="server" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">Servers</TabsTrigger>
          <TabsTrigger value="chef" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">Kitchen</TabsTrigger>
          <TabsTrigger value="bartender" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">Bartenders</TabsTrigger>
          <TabsTrigger value="setup" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">Setup</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStaff.map((s) => (
          <Card key={s.id} className="border-none shadow-lg" data-testid={`staff-card-${s.id}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {s.name.charAt(0)}
                  </div>
                  <div>
                    <CardTitle className="text-lg text-[#1e3a5f]">{s.name}</CardTitle>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${roleColors[s.role] || 'bg-gray-100 text-gray-800'}`}>
                      {s.role.charAt(0).toUpperCase() + s.role.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Edit size={16} /></Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(s.id)} data-testid={`delete-staff-${s.id}`}><Trash2 size={16} /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {s.email && <p className="flex items-center gap-2 text-gray-600"><Mail size={14} />{s.email}</p>}
                {s.phone && <p className="flex items-center gap-2 text-gray-600"><Phone size={14} />{s.phone}</p>}
                {s.hourly_rate && <p className="font-semibold text-[#1e3a5f]">${s.hourly_rate}/hour</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStaff.length === 0 && <div className="text-center py-20"><Users className="mx-auto text-gray-300 mb-4" size={48} /><p className="text-gray-500">No staff members found</p></div>}
    </div>
  );
};

export default Staff;
